import { useEffect, useState } from "react";
import { X, ChevronLeft, HelpCircle } from "lucide-react";
import { useSnapshot } from "valtio";
import CustomDrawer from "./CustomDrawer";
import SupportChat from "./dashboard/support-chat";
import { FaqList } from "./dashboard/faqs";
import { supportStore, closeSupportDrawer } from "../utils/support";

type View = "chat" | "faq";

// SupportDrawer is the unified support entry: opening support launches the chat
// directly in a drawer, with a FAQ button in the header that swaps the body to
// the FAQ list (and a back arrow to return to the chat).
const SupportDrawer = () => {
  const { drawerOpen } = useSnapshot(supportStore);
  const [view, setView] = useState<View>("chat");

  // Always reopen on the chat view and trigger SkippWidget if available.
  useEffect(() => {
    if (drawerOpen) {
      setView("chat");
      if (typeof window !== "undefined" && window.SkippWidget?.open) {
        window.SkippWidget.open();
      }
    }
  }, [drawerOpen]);

  const handleClose = () => {
    if (typeof window !== "undefined" && window.SkippWidget?.close) {
      window.SkippWidget.close();
    }
    closeSupportDrawer();
  };

  const content = (
    <div className="flex flex-col h-full w-full bg-[#F9F9F9] font-[glacial]">
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-3 border-b border-gray-200 bg-white">
        {view === "faq" ? (
          <button
            onClick={() => setView("chat")}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Back to chat"
          >
            <ChevronLeft size={22} className="text-gray-900" />
          </button>
        ) : (
          <button
            onClick={() => setView("faq")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-900 text-xs hover:bg-gray-200 transition-colors"
          >
            <HelpCircle size={15} />
            <span>FAQ</span>
          </button>
        )}

        <h1 className="text-base font-[optima] text-gray-900">
          {view === "faq" ? "FAQs" : "Support @ Weddn"}
        </h1>

        <button
          onClick={handleClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close support"
        >
          <X size={22} className="text-gray-900" />
        </button>
      </div>


      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {view === "chat" ? <SupportChat /> : <FaqList />}
      </div>
    </div>
  );

  return (
    <CustomDrawer
      isOpen={drawerOpen}
      onClose={handleClose}
      placement="right"

      size="md"
      h="100vh"
      borderTopRadius="none"
      content={content}
    />
  );
};

export default SupportDrawer;
